import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { TolgeeProvider } from "@tolgee/react";
import { createStore, getDefaultStore, Provider as JotaiProvider } from "jotai";
import { RouteError } from "./components/route-error";
import { initSentry } from "./integrations/sentry/sentry";
import { createQueryClient } from "./integrations/tanstack-query/root-provider";
import {
	createTolgee,
	getClientTolgee,
	TOLGEE_STATIC_DATA,
} from "./integrations/tolgee/tolgee-instance";
import { localeFromPath } from "./lib/locale";
import { routeTree } from "./routeTree.gen";
import { localeAtom } from "./stores/locale-store";

/**
 * 서버는 요청마다 호출된다(Start 서버 핸들러). Workers 아이솔레이트는 요청 간 재사용되므로
 * QueryClient·Tolgee·Jotai 스토어를 모듈 싱글턴으로 두면 한 방문자의 캐시/언어가 다른
 * 방문자의 HTML에 섞인다 — 셋 모두 여기서 만들어 라우터 컨텍스트로 내린다.
 * 클라이언트는 한 번 호출되며 기존과 같은 단일 인스턴스를 쓴다(Jotai는 기본 스토어 —
 * last-trade-symbol이 getDefaultStore()로 직접 읽는 스토어와 동일해야 한다).
 */
export function getRouter() {
	const isServer = typeof window === "undefined";
	// 초기 로케일 = URL 프리픽스 ?? "en" — 서버/클라이언트 첫 렌더 일치(hydration).
	// 서버의 실제 값은 __root beforeLoad가 요청 경로로 설정하고, 저장된 선호(zh 등)는
	// 마운트 후 LocaleSync가 적용한다.
	const initialLocale = isServer
		? "en"
		: (localeFromPath(window.location.pathname) ?? "en");
	const queryClient = createQueryClient();
	const tolgee = isServer ? createTolgee(initialLocale) : getClientTolgee();
	const store = isServer ? createStore() : getDefaultStore();
	store.set(localeAtom, initialLocale);

	const router = createTanStackRouter({
		routeTree,
		context: { queryClient, tolgee, store },
		defaultErrorComponent: RouteError,
		scrollRestoration: true,
		defaultPreload: "intent",
		defaultPreloadStaleTime: 0,
		pathParamsAllowedCharacters: [":"],
		// 프로바이더 스택 (QueryClientProvider는 아래 ssr-query 통합이 자동으로 감싼다).
		Wrap: ({ children }) => (
			<JotaiProvider store={store}>
				<TolgeeProvider
					tolgee={tolgee}
					fallback={null}
					// ssr: 첫 렌더에서 staticData+언어를 동기 적용해 isLoaded()를 즉시
					// true로 만든다. 없으면 서버가 fallback(null)만 렌더해 <html> 전체가
					// 빈 문서가 된다. 언어는 인스턴스 현재값(서버: beforeLoad가 설정).
					ssr={{
						language: tolgee.getLanguage() ?? initialLocale,
						staticData: TOLGEE_STATIC_DATA,
					}}
				>
					{children}
				</TolgeeProvider>
			</JotaiProvider>
		),
	});

	// 로더의 ensureQueryData 결과를 dehydrate → 클라이언트 캐시로 hydrate. QueryClientProvider 주입 포함.
	setupRouterSsrQueryIntegration({ router, queryClient });

	// 클라이언트 라우터 생성 직후 = 구 main.tsx와 동일한 init 타이밍.
	if (!isServer) {
		initSentry(router);
	}

	return router;
}

declare module "@tanstack/react-router" {
	interface Register {
		router: ReturnType<typeof getRouter>;
	}
}
